/* Dustyrosa UI helpers
   1) --dr-ann-h: height of the announcement bar, so the hero fits the visible screen exactly.
   2) Page-change loader: round seal on a soft rose background (internal navigation + first visit of a session). */
(() => {
  const root = document.documentElement;

  /* 1) announcement bar height */
  const setAnn = () => {
    const bar = document.querySelector('.dr-announcement-bar');
    const h = bar && bar.offsetParent !== null ? Math.round(bar.getBoundingClientRect().height) : 0;
    root.style.setProperty('--dr-ann-h', `${h}px`);
  };
  setAnn();
  window.addEventListener('resize', setAnn, { passive: true });
  window.addEventListener('load', setAnn);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(setAnn);
  if ('ResizeObserver' in window) {
    const watch = () => { const bar = document.querySelector('.dr-announcement-bar'); if (bar) new ResizeObserver(setAnn).observe(bar); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  }

  /* 2) loader */
  const loader = document.getElementById('dr-loader');
  if (!loader) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MIN_SHOW = 650;
  const MAX_SHOW = 4000;
  const shownAt = performance.now();
  let hidden = false;

  const hide = () => {
    if (hidden) return;
    hidden = true;
    const wait = Math.max(0, MIN_SHOW - (performance.now() - shownAt));
    setTimeout(() => {
      root.classList.remove('dr-loading');
      root.classList.add('dr-loaded');
      setTimeout(() => { loader.hidden = true; }, 700);
    }, wait);
  };

  if (root.classList.contains('dr-loading')) {
    if (document.readyState === 'complete') hide(); else window.addEventListener('load', hide, { once: true });
    setTimeout(hide, MAX_SHOW);
  } else {
    loader.hidden = true;
  }

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) { hidden = true; root.classList.remove('dr-loading'); loader.hidden = true; }
  });

  document.addEventListener('click', (e) => {
    if (reduce || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('aria-controls') || a.hasAttribute('data-dr-open-delivery') || a.closest('[data-no-loader]')) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    if (!/^https?:$/.test(url.protocol)) return;
    if (url.pathname === location.pathname && url.search === location.search) return;
    if (/\/cart\/|\/checkouts?\/|\/account\/(logout)/.test(url.pathname)) return;
    try { sessionStorage.setItem('drNav', '1'); } catch (_) {}
    hidden = false;
    loader.hidden = false;
    requestAnimationFrame(() => root.classList.add('dr-leaving'));
    setTimeout(() => root.classList.remove('dr-leaving'), 6000);
  });
})();


/* Main menu: the image only changes after a short, deliberate hover on the link text */
(() => {
  const DWELL = 260;
  let timer = null;
  const menu = () => document.getElementById('menu_mobile');
  document.addEventListener('mouseover', (e) => {
    const m = menu();
    if (!m || !e.target.closest) return;
    const link = e.target.closest('#menu_mobile .dr-menu__primary a[data-img]');
    if (link) {
      clearTimeout(timer);
      timer = setTimeout(() => m.setAttribute('data-dr-hover', link.getAttribute('data-img')), DWELL);
      return;
    }
    if (!e.target.closest('#menu_mobile .dr-menu__primary')) return;
    clearTimeout(timer);
  });
  document.addEventListener('mouseout', (e) => {
    const m = menu();
    if (!m || !e.target.closest || !e.target.closest('#menu_mobile .dr-menu__primary a[data-img]')) return;
    clearTimeout(timer);
    if (!e.relatedTarget || !e.relatedTarget.closest || !e.relatedTarget.closest('#menu_mobile .dr-menu__primary')) {
      timer = setTimeout(() => m.removeAttribute('data-dr-hover'), 500);
    }
  });
})();

/* Home: quiet reveal on scroll and a slow parallax on the editorial photos */
(() => {
  if (!document.body.classList.contains('dr-page-type-index')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
  if (window.Shopify && window.Shopify.designMode) return;
  const root = document.documentElement;
  const groups = [
    '.dr-feature__inner > *',
    '.dr-cats .dr-head > *',
    '.dr-cat',
    '.dr-pillars__item',
    '.dr-cta > *',
    '.dr-quote > *'
  ];
  const items = [];
  groups.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      const sibs = Array.from(el.parentElement.children).filter((c) => c.matches(sel));
      el.style.setProperty('--dr-d', Math.min(sibs.indexOf(el), 4) * 0.12 + 's');
      el.classList.add('dr-rv');
      items.push(el);
    });
  });
  const medias = Array.from(document.querySelectorAll('.dr-feature__media'));
  root.classList.add('dr-motion');
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  items.concat(medias).forEach((el) => io.observe(el));

  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    medias.forEach((m) => {
      const r = m.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const p = Math.max(-1, Math.min(1, ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2)));
      const img = m.querySelector('img');
      if (img) img.style.transform = 'translate3d(0,' + (-p * r.height * 0.06).toFixed(1) + 'px,0)';
    });
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
})();

/* Size guide: size pills, cm/in switch and numbered measurement list driven by the page's own table */
(() => {
  const FIG = '.dr-sg';
  
  const toIn = (txt) => txt.replace(/\d+(?:[.,]\d+)?/g, (m) => {
    const v = parseFloat(m.replace(',', '.')) / 2.54;
    return (Math.round(v * 10) / 10).toString();
  });
  const build = (root) => {
    if (root.dataset.ready) return;
    const table = root.querySelector('table');
    if (!table) return;
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length < 2) return;
    const heads = Array.from(rows[0].children).slice(1).map((c) => c.textContent.trim());
    const data = rows.slice(1).map((r) => {
      const c = Array.from(r.children);
      return { label: c[0].textContent.trim(), vals: c.slice(1).map((x) => x.textContent.trim()) };
    });
    const ctl = root.querySelector('.dr-sg__ctl');
    const list = root.querySelector('.dr-sg__list');
    if (!ctl || !list || !heads.length) return;
    let size = heads.indexOf('M') >= 0 ? heads.indexOf('M') : 0;
    let unit = 'cm';
    const pills = document.createElement('div'); pills.className = 'dr-sg__pills'; pills.setAttribute('role', 'group');
    pills.setAttribute('aria-label', root.dataset.sizeLabel || 'Size');
    const units = document.createElement('div'); units.className = 'dr-sg__pills dr-sg__units'; units.setAttribute('role', 'group');
    const mk = (txt, cb) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'dr-sg__pill'; b.textContent = txt; b.addEventListener('click', cb); return b; };
    const sizeBtns = heads.map((h, i) => { const b = mk(h, () => { size = i; render(); }); pills.appendChild(b); return b; });
    const unitBtns = ['cm', 'in'].map((u) => { const b = mk(u, () => { unit = u; render(); }); units.appendChild(b); return b; });
    ctl.appendChild(pills); ctl.appendChild(units);
    data.forEach((d, i) => {
      const li = document.createElement('li'); li.dataset.k = String(i + 1);
      li.innerHTML = '<span class="n">' + (i + 1) + '</span><span class="l"></span><span class="v"></span>';
      li.querySelector('.l').textContent = d.label;
      list.appendChild(li);
    });
    const lines = Array.from(root.querySelectorAll('.dr-sg__ln'));
    const light = (k, on) => {
      lines.forEach((l) => l.classList.toggle('is-on', on && l.dataset.k === k));
      Array.from(list.children).forEach((li) => li.classList.toggle('is-on', on && li.dataset.k === k));
    };
    Array.from(list.children).forEach((li) => {
      li.addEventListener('mouseenter', () => light(li.dataset.k, true));
      li.addEventListener('mouseleave', () => light(li.dataset.k, false));
    });
    lines.forEach((l) => {
      l.addEventListener('mouseenter', () => light(l.dataset.k, true));
      l.addEventListener('mouseleave', () => light(l.dataset.k, false));
    });
    // hide drawing lines that have no matching table row
    lines.forEach((l) => { if (+l.dataset.k > data.length) l.style.display = 'none'; });
    function render() {
      sizeBtns.forEach((b, i) => b.setAttribute('aria-pressed', String(i === size)));
      unitBtns.forEach((b, i) => b.setAttribute('aria-pressed', String(['cm', 'in'][i] === unit)));
      Array.from(list.children).forEach((li, i) => {
        const raw = data[i].vals[size] || '';
        li.querySelector('.v').textContent = unit === 'cm' ? raw + ' cm' : toIn(raw) + ' in';
      });
    }
    render();
    root.dataset.ready = '1';
  };
  const init = () => document.querySelectorAll(FIG).forEach(build);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* "Shipping" in the price note opens the delivery & return sheet of the same product */
document.addEventListener('click', (e) => {
  const link = e.target.closest && e.target.closest('a[data-dr-open-delivery]');
  if (!link) return;
  const scope = link.closest('.dr-product-info__list, .dr-group-product-block, section') || document;
  const btn = scope.querySelector('.dr-pr-popup__delivery') || document.querySelector('.dr-pr-popup__delivery');
  if (!btn) return;
  e.preventDefault();
  btn.click();
});

/* Wishlist page: "Remove all" in the bar */
(() => {
  const clear = document.querySelector('.dr-wl-bar .dr-wl-clear');
  if (!clear) return;
  clear.addEventListener('click', () => {
    try { localStorage.setItem('theme4:wishlist:id', ''); } catch (_) {}
    location.href = location.pathname + '?view=wishlist';
  });
})();

/* Sets: size pills inside the "this set consists of two pieces" element drive the real (visually hidden) option pickers */
(() => {
  const findPicker = (box) => {
    let el = box.parentElement;
    for (let i = 0; el && i < 8; i++, el = el.parentElement) {
      const p = el.querySelector('dr-variant-picker');
      if (p) return p;
    }
    return null;
  };
  const setup = (box) => {
    if (box.dataset.drReady) return;
    const picker = findPicker(box);
    const groups = box.querySelectorAll('.dr-bundle__sizes');
    if (!picker || !groups.length) return;
    box.dataset.drReady = '1';
    const input = (opt, value) => {
      const fs = picker.querySelector('fieldset[data-index="' + opt + '"]');
      if (!fs) return null;
      return [...fs.querySelectorAll('input[type="radio"]')].find((i) => i.value === value) || null;
    };
    const sync = () => {
      groups.forEach((g) => {
        const opt = g.dataset.opt;
        g.querySelectorAll('.dr-bundle__pill').forEach((b) => {
          const i = input(opt, b.dataset.value);
          b.setAttribute('aria-checked', i && i.checked ? 'true' : 'false');
          b.classList.toggle('is-unavailable', !!(i && i.classList.contains('is-unavailable')));
          b.classList.toggle('is-disabled', !!(i && i.classList.contains('is-disabled')));
        });
      });
    };
    box.addEventListener('click', (e) => {
      const b = e.target.closest('.dr-bundle__pill');
      if (!b) return;
      const i = input(b.closest('.dr-bundle__sizes').dataset.opt, b.dataset.value);
      if (!i) return;
      const lab = picker.querySelector('label[for="' + i.id + '"]');
      (lab || i).click();
      setTimeout(sync, 0);
      setTimeout(sync, 250);
    });
    picker.addEventListener('change', () => { sync(); setTimeout(sync, 250); });
    new MutationObserver(sync).observe(picker, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'checked', 'selected-variant'] });
    groups.forEach((g) => g.removeAttribute('hidden'));
    sync();
    picker.classList.add('dr-bundle-on');
  };
  const scan = () => document.querySelectorAll('.dr-bundle').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  /* quick view markup is injected later */
  new MutationObserver((muts) => {
    for (const m of muts) for (const n of m.addedNodes) {
      if (n.nodeType === 1 && (n.matches('.dr-bundle') || n.querySelector('.dr-bundle'))) { scan(); return; }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* heavy, rarely used lists (country selector, search suggestions) wait in a <template> and are inserted the first time their trigger is touched */
(() => {
  const fill = (id) => {
    document.querySelectorAll('template[data-dr-lazy="' + id + '"]').forEach((tpl) => tpl.replaceWith(tpl.content));
  };
  const trigger = (e) => {
    const t = e.target && e.target.closest && e.target.closest('[aria-controls]');
    if (t) fill(t.getAttribute('aria-controls'));
  };
  document.addEventListener('pointerover', trigger, { capture: true, passive: true });
  document.addEventListener('focusin', trigger, true);
  document.addEventListener('click', trigger, true);
  document.addEventListener('keydown', trigger, true);
})();

/* filter drawer: choices only count once "ansehen" is pressed; the button shows how many pieces match while you choose */
(() => {
  let timer = null;
  let seq = 0;
  const form = () => document.getElementById('dr-facet-filters-form');
  const params = (f) => {
    const p = new URLSearchParams();
    new FormData(f).forEach((v, k) => { if (String(v) !== '') p.append(k, v); });
    p.delete('page');
    return p;
  };
  const cache = new Map();
  const setCount = (dst, text) => {
    dst.classList.remove('is-pending');
    if (dst.textContent === text) return;
    if (!dst.animate) { dst.textContent = text; return; }
    dst.getAnimations().forEach((a) => a.cancel());
    dst.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-5px)' }], { duration: 110, easing: 'ease-in', fill: 'forwards' });
    setTimeout(() => {
      dst.getAnimations().forEach((a) => a.cancel());
      dst.textContent = text;
      dst.animate([{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 260, easing: 'cubic-bezier(.22,1,.36,1)' });
    }, 110);
  };
  const preview = () => {
    const f = form();
    const dst = document.querySelector('#dr-filter_form [data-dr-count]');
    if (!f || !dst) return;
    const me = ++seq;
    const url = (f.getAttribute('action') || location.pathname) + '?' + params(f).toString();
    if (cache.has(url)) { setCount(dst, cache.get(url)); return; }
    dst.classList.add('is-pending');
    fetch(url, { headers: { Accept: 'text/html' } }).then((r) => r.text()).then((html) => {
      if (me !== seq) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const n = doc.querySelector('.dr-active-filters__count, .dr-count');
      if (n && n.textContent.trim()) { cache.set(url, n.textContent.trim()); setCount(dst, n.textContent.trim()); return; }
      /* no active filter text on the page: use the number of cards, keeping the localized wording */
      const cards = doc.querySelectorAll('.dr-card-product').length;
      const txt = dst.textContent.replace(/\d+/, String(cards));
      cache.set(url, txt);
      setCount(dst, txt);
    }).catch(() => dst.classList.remove('is-pending'));
  };
  const queue = (wait) => { clearTimeout(timer); const d = typeof wait === 'number' ? wait : 0; if (d) timer = setTimeout(preview, d); else preview(); };
  document.addEventListener('change', (e) => { if (e.target.closest && e.target.closest('#dr-filter_form')) queue(0); });
  document.addEventListener('input', (e) => { if (e.target.closest && e.target.closest('#dr-filter_form') && e.target.matches('input[type="range"], input[type="number"], input[type="text"]')) queue(180); });
  /* the footer button applies the filters; the drawer then stays closed after the page has re-rendered */
  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('.dr-filter-foot__show');
    if (!b) return;
    const f = form();
    if (!f) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (f.requestSubmit) f.requestSubmit(); else f.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    setTimeout(() => {
      const dlg = document.getElementById('dr-filter_form');
      if (dlg && dlg.open && dlg.parentElement && typeof dlg.parentElement.close === 'function') dlg.parentElement.close();
    }, 900);
  }, true);
})();

/* family illustration: reveal once when it scrolls into view */
(() => {
  const init = () => {
    document.querySelectorAll('.dr-family:not([data-dr-fam])').forEach((el) => {
      el.setAttribute('data-dr-fam', '1');
      if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      el.classList.add('js-anim');
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { el.classList.add('is-in'); io.disconnect(); }
      }, { threshold: 0.35 });
      io.observe(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* contact page: staged reveal when it scrolls into view */
(() => {
  const init = () => {
    document.querySelectorAll('.dr-contact:not([data-dr-c])').forEach((el) => {
      el.setAttribute('data-dr-c', '1');
      if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      el.classList.add('js-anim');
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { el.classList.add('is-in'); io.disconnect(); }
      }, { threshold: 0.18 });
      io.observe(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* help pages: every topic becomes a group (heading sticks on the left while its content scrolls), blocks rise in */
(() => {
  const init = () => {
    const rte = document.querySelector('html[data-help="shipping"] .dr-main-page .dr-rte, html[data-help="returns-withdrawal"] .dr-main-page .dr-rte, html[data-help="materials-care"] .dr-main-page .dr-rte');
    if (!rte || rte.dataset.drRise) return;
    rte.dataset.drRise = '1';
    let cur = null;
    let body = null;
    Array.from(rte.children).forEach((k) => {
      if (k.tagName === 'H4') {
        cur = document.createElement('div');
        cur.className = 'dr-hgroup';
        body = document.createElement('div');
        body.className = 'dr-hgroup__body';
        rte.insertBefore(cur, k);
        cur.appendChild(k);
        cur.appendChild(body);
      } else if (body) {
        body.appendChild(k);
      }
    });
    rte.classList.add('dr-hgrouped');
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    rte.classList.add('js-rise');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    const seen = new WeakSet();
    const watch = () => rte.querySelectorAll(':scope > p, :scope > .dr-hgroup > *').forEach((c, i) => {
      if (seen.has(c)) return;
      seen.add(c);
      c.style.transitionDelay = ((i % 3) * 0.08) + 's';
      io.observe(c);
    });
    watch();
    /* the theme wraps tables after load: observe the new wrappers too */
    new MutationObserver(watch).observe(rte, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* legal pages: contents list that follows the reading position */
(() => {
  const init = () => {
    if (!document.documentElement.hasAttribute('data-legal')) return;
    const rte = document.querySelector('.dr-main-page .dr-rte');
    if (!rte || rte.querySelector('.dr-toc')) return;
    const heads = Array.from(rte.querySelectorAll(':scope > h3'));
    if (heads.length < 3) return;
    const nav = document.createElement('nav');
    nav.className = 'dr-toc';
    const label = (window.drLegalToc || 'On this page');
    nav.innerHTML = '<p class="dr-toc__title"></p><ol></ol>';
    nav.querySelector('.dr-toc__title').textContent = document.documentElement.getAttribute('data-toc') || label;
    const ol = nav.querySelector('ol');
    heads.forEach((h, i) => {
      if (!h.id) h.id = 'dr-sec-' + (i + 1);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.replace(/^\s*\d+\.\s*/, '').trim() || h.textContent.trim();
      li.appendChild(a); ol.appendChild(li);
    });
    const upd = document.documentElement.getAttribute('data-updated');
    if (upd) {
      const p = document.createElement('p');
      p.className = 'dr-toc__updated';
      p.textContent = upd;
      nav.appendChild(p);
    }
    rte.insertBefore(nav, rte.firstChild);
    if (!('IntersectionObserver' in window)) return;
    const links = Array.from(ol.querySelectorAll('a'));
    /* one bar slides from entry to entry; the active entry follows the scroll position, so the very top always shows the first one */
    const bar = document.createElement('span');
    bar.className = 'dr-toc__bar';
    bar.setAttribute('aria-hidden', 'true');
    ol.appendChild(bar);
    const moveBar = (a, animate) => {
      if (!a) return;
      if (!animate) bar.style.transition = 'none';
      const top = a.getBoundingClientRect().top - ol.getBoundingClientRect().top;
      bar.style.height = a.offsetHeight + 'px';
      bar.style.transform = 'translateY(' + Math.round(top) + 'px)';
      bar.style.opacity = '1';
      if (!animate) { void bar.offsetWidth; bar.style.transition = ''; }
    };
    let current = -1;
    let lock = -1;
    let settledY = 0;
    let idleTimer = null;
    let settled = true;
    const natural = () => {
      const line = Math.min(window.innerHeight * 0.3, 260);
      let idx = 0;
      heads.forEach((h, i) => { if (h.getBoundingClientRect().top <= line) idx = i; });
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4) idx = heads.length - 1;
      if (window.scrollY < 4) idx = 0;
      return idx;
    };
    const show = (idx, animate) => {
      if (idx === current) return;
      current = idx;
      links.forEach((l, i) => l.classList.toggle('is-active', i === idx));
      moveBar(links[idx], animate);
    };
    const update = (animate) => {
      if (animate === 'init') { current = -1; lock = -1; show(natural(), false); return; }
      if (lock >= 0) {
        /* a click on the list is respected until the reader scrolls on by hand */
        if (settled && Math.abs(window.scrollY - settledY) > 8) lock = -1;
        else { show(lock, true); return; }
      }
      show(natural(), true);
    };
    links.forEach((l, i) => l.addEventListener('click', () => {
      lock = i;
      settled = false;
      show(i, true);
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { settled = true; settledY = window.scrollY; }, 900);
    }));
    const onScroll = () => {
      update(true);
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { settled = true; settledY = window.scrollY; }, 180);
    };
    let last = 0;
    let timer = null;
    window.addEventListener('scroll', () => {
      const now = Date.now();
      if (now - last > 50) { last = now; onScroll(); return; }
      clearTimeout(timer);
      timer = setTimeout(() => { last = Date.now(); onScroll(); }, 60);
    }, { passive: true });
    ['wheel', 'touchstart', 'keydown'].forEach((ev) => window.addEventListener(ev, () => { if (lock >= 0 && settled) lock = -1; }, { passive: true }));
    window.addEventListener('resize', () => update('init'));
    update('init');
    window.addEventListener('load', () => { current = -1; update('init'); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { current = -1; update('init'); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* legal pages: the translated update date lives in the left rail; the old in-text line goes; Impressum keeps the legal basis with the provider */
(() => {
  const UPDATED = /^(Zuletzt aktualisiert|Stand|Last updated|Last update|Última actualización|Dernière mise à jour|最終更新)\s*[:：]/i;
  const BASIS = /(§\s*5\b|DDG|TMG)/;
  const PROVIDER = ['anbieter', 'provider', 'proveedor', 'fournisseur', '事業者', '運営者'];
  const init = () => {
    const root = document.documentElement;
    if (!root.hasAttribute('data-legal')) return;
    const rte = document.querySelector('.dr-main-page .dr-rte');
    const toc = rte && rte.querySelector(':scope > .dr-toc');
    if (!rte || !toc) return;
    const paragraphs = Array.from(rte.querySelectorAll(':scope > p'));
    const updated = paragraphs.find((p) => UPDATED.test(p.textContent.trim()));
    if (updated) {
      if (toc.querySelector('.dr-toc__updated')) updated.remove();
      else if (!updated.classList.contains('dr-toc__updated')) {
        updated.classList.add('dr-toc__updated');
        toc.appendChild(updated);
      }
    }
    if (root.getAttribute('data-help') !== 'impressum') return;
    const legalBasis = paragraphs.find((p) => BASIS.test(p.textContent.trim().slice(0, 60)));
    const provider = Array.from(rte.querySelectorAll(':scope > h3, :scope > h4')).find((h) => PROVIDER.includes(h.textContent.trim().toLocaleLowerCase()));
    if (legalBasis && provider && !legalBasis.classList.contains('dr-impressum__basis')) {
      legalBasis.classList.add('dr-impressum__basis');
      provider.insertAdjacentElement('afterend', legalBasis);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();


/* cart page: note, shipping and discount open as panels inside the summary card, exactly like in the drawer */
(function () {
  'use strict';
  var ICONS = {
    note: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 4h8l3 3v13H5V4z"/><path d="M8 4v4h8"/><path d="M9 12h6M9 16h4"/></svg>',
    rates: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6h12v10H2zM14 9h4l3 3v4h-7"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="17" cy="17.5" r="1.6"/></svg>',
    discount: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12.5V4h8.5L21 13.5 13.5 21z"/><circle cx="7.6" cy="8.1" r="1.2"/></svg>'
  };
  var setup = function () {
    var foot = document.querySelector('.dr-page-cart__footer');
    if (!foot || foot.querySelector('.dr-page-tools')) return;
    var host = foot.querySelector('.dr-cart-tax');
    var note = foot.querySelector('dr-cart-note');
    var disc = foot.querySelector('dr-cart-discount');
    var calc = document.querySelector('dr-main-cart dr-shipping-calculator');
    if (!host || (!note && !disc && !calc)) return;
    var txt = function (el, sel) { var n = el && el.querySelector(sel); return n ? n.textContent.trim() : ''; };
    var defs = [];
    var noteText = function () { return txt(note, 'label > span:not([hidden])') || txt(note, 'label'); };
    if (note) defs.push({ key: 'note', id: 'dialog-is--note', el: note, label: noteText() });
    if (calc) defs.push({ key: 'rates', id: 'dialog-is--rates', el: calc, label: txt(calc, ':scope > h3') });
    if (disc) defs.push({ key: 'discount', id: 'dialog-is--discount', el: disc, label: txt(disc, 'label') || (disc.querySelector('input') || {}).placeholder || '' });
    var tools = document.createElement('div');
    tools.className = 'dr-page-tools dr-mini-cart__tool';
    var panels = document.createElement('div');
    panels.className = 'dr-tool-panels dr-page-tool-panels';
    defs.forEach(function (d) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dr-mini-cart__tool_btn';
      b.setAttribute('aria-controls', d.id);
      b.setAttribute('aria-expanded', 'false');
      if (d.label) { b.setAttribute('aria-label', d.label); b.setAttribute('data-tip', d.label); }
      b.innerHTML = ICONS[d.key];
      tools.appendChild(b);
      if (d.key === 'note') {
        var refresh = function () {
          var s = noteText();
          if (s) { b.setAttribute('aria-label', s); b.setAttribute('data-tip', s); }
        };
        d.el.addEventListener('change', function () { setTimeout(refresh, 60); });
      }
      var panel = document.createElement('div');
      panel.className = 'dr-tool-panel';
      panel.setAttribute('data-for', d.id);
      var inner = document.createElement('div');
      inner.className = 'dr-tool-panel__in';
      inner.appendChild(d.el);
      panel.appendChild(inner);
      panels.appendChild(panel);
    });
    host.insertAdjacentElement('afterend', panels);
    host.insertAdjacentElement('afterend', tools);
    var col = foot.querySelector('.dr-page-cart__footer-wrap > .dr-col:not(:has(.dr-main-cart__button-checkout))');
    if (col && !col.children.length) col.classList.add('dr-is-empty');
  };
  var t;
  var run = function () { clearTimeout(t); t = setTimeout(setup, 60); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('cart:main:render', run);
  document.addEventListener('cart:update', run);
})();

/* cart: the same colour of both single pieces becomes the set automatically */
(function () {
  'use strict';
  var el = document.getElementById('dr-set-map');
  if (!el) return;
  var data;
  try { data = JSON.parse(el.textContent); } catch (e) { return; }
  if (!data.sets || !data.sets.length) return;
  var busy = false;
  var money = function (c) {
    try { return (c / 100).toLocaleString(document.documentElement.lang || 'de', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }); }
    catch (e) { return (c / 100).toFixed(2); }
  };
  var toast = function (text) {
    var t = document.createElement('div');
    t.className = 'dr-toast';
    t.setAttribute('role', 'status');
    t.textContent = text;
    t.setAttribute('popover', 'manual');
    document.body.appendChild(t);
    try { t.showPopover(); } catch (e) {}
    requestAnimationFrame(function () { t.classList.add('is-in'); });
    setTimeout(function () { t.classList.remove('is-in'); setTimeout(function () { t.remove(); }, 600); }, 6500);
  };
  var merge = function () {
    if (busy) return Promise.resolve(false);
    busy = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
      var notes = [], updates = {}, adds = [], changed = false;
      data.sets.forEach(function (set) {
        var byColor = {};
        cart.items.forEach(function (it) {
          var idx = set.parts.indexOf(it.handle);
          if (idx < 0 || !it.options_with_values) return;
          var opts = (it.options_with_values || []).map(function (o) { return o.value; });
          var color = opts[0];
          (byColor[color] = byColor[color] || [[], []])[idx].push({ it: it, size: opts[1] });
        });
        Object.keys(byColor).forEach(function (color) {
          var a = byColor[color][0], b = byColor[color][1];
          if (!a.length || !b.length) return;
          var x = a[0], y = b[0];
          var n = Math.min(updates[x.it.key] === undefined ? x.it.quantity : updates[x.it.key], updates[y.it.key] === undefined ? y.it.quantity : updates[y.it.key]);
          var v = set.variants.filter(function (s) { return s.ok && s.o[0] === color && s.o[1] === x.size && s.o[2] === y.size; })[0];
          if (!v || n < 1) return;
          updates[x.it.key] = (updates[x.it.key] === undefined ? x.it.quantity : updates[x.it.key]) - n;
          updates[y.it.key] = (updates[y.it.key] === undefined ? y.it.quantity : updates[y.it.key]) - n;
          adds.push({ id: v.id, quantity: n });
          var saved = (x.it.final_price + y.it.final_price - v.price) * n;
          notes.push(data.msg.replace('{title}', set.title + ' · ' + color).replace('{saved}', money(Math.max(saved, 0))));
          changed = true;
        });
      });
      if (!changed) return false;
      var rows = [];
      cart.items.forEach(function (it, i) {
        if (updates[it.key] === undefined) return;
        document.querySelectorAll('.dr-cart-item, .dr-mini-cart__item').forEach(function (r) {
          if (r.querySelector('[data-index="' + (i + 1) + '"]')) rows.push(r);
        });
      });
      rows.forEach(function (r) { window.__drCollapse(r); });
      var wait = new Promise(function (res) { setTimeout(res, rows.length ? 420 : 0); });
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: adds }) })
        .then(function (r) { if (!r.ok) throw new Error('add'); return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: updates }) }); })
        .then(function (r) { if (!r.ok) throw new Error('update'); return wait; }).then(function () { document.dispatchEvent(new CustomEvent('cart:reload')); toast(notes[0]); return true; });
    }).catch(function () { return false; }).then(function (v) { busy = false; return v; });
  };
  window.__drMerge = merge;
  var timer;
  var schedule = function () { clearTimeout(timer); timer = setTimeout(merge, 150); };
  document.addEventListener('cart:update', schedule);
  /* new lines fade in */
  var sig = function (r) {
    // the variant link is part of the server markup, so a line that is only re-rendered (quantity change) keeps its signature;
    // chips and labels are added by script a moment later and made re-rendered lines look new (row-in replay, the list collapsed for a frame)
    var link = r.querySelector('a.dr-page-cart__title[href], a.dr-mini-cart__title[href]');
    if (link) return link.getAttribute('href');
    var t = r.querySelector('.dr-page-cart__title, .dr-mini-cart__title');
    var set = r.querySelector('.dr-cartset');
    var v;
    if (set) {
      v = Array.prototype.map.call(set.querySelectorAll('.dr-cartset__color strong, .dr-cartset__name, .dr-cartset__size'), function (x) { return x.textContent.replace(/\s+/g, ' ').trim(); }).join('~');
    } else {
      var chips = r.querySelectorAll('.dr-vedit .dr-vchip');
      if (chips.length) {
        v = Array.prototype.map.call(chips, function (x) { return (x.getAttribute('title') || x.textContent).replace(/\s+/g, ' ').trim(); }).join('~');
      } else {
        var m = r.querySelector('.dr-page-cart__meta-variant, .dr-mini-cart__meta-variant');
        v = m ? m.textContent.replace(/\s+/g, ' ').trim() : '';
      }
    }
    return (t ? t.textContent.trim() : '') + '|' + v;
  };
  var seen = {};
  var rowsNow = function () { return document.querySelectorAll('.dr-cart-item, .dr-mini-cart__item'); };
  var primed = false;
  var mo = new MutationObserver(function () {
    var cur = {};
    rowsNow().forEach(function (r) {
      var s = sig(r); cur[s] = true;
      if (primed && !seen[s] && !r.classList.contains('dr-row-in')) {
        r.classList.add('dr-row-in');
        setTimeout(function () { r.classList.remove('dr-row-in'); }, 900);
      }
    });
    seen = cur;
  });
  var prime = function () { rowsNow().forEach(function (r) { seen[sig(r)] = true; }); primed = true; };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { prime(); mo.observe(document.body, { childList: true, subtree: true }); }); else { prime(); mo.observe(document.body, { childList: true, subtree: true }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule); else schedule();
})();


/* cart: take one piece out of a set, the other one stays as a single item */
(function () {
  'use strict';
  var el = document.getElementById('dr-set-map');
  if (!el) return;
  var data;
  try { data = JSON.parse(el.textContent); } catch (e) { return; }
  var busy = false;
  var toast = function (text) {
    var t = document.createElement('div');
    t.className = 'dr-toast';
    t.setAttribute('role', 'status');
    t.textContent = text;
    t.setAttribute('popover', 'manual');
    document.body.appendChild(t);
    try { t.showPopover(); } catch (e) {}
    requestAnimationFrame(function () { t.classList.add('is-in'); });
    setTimeout(function () { t.classList.remove('is-in'); setTimeout(function () { t.remove(); }, 600); }, 6000);
  };
  var post = function (url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then(function (r) { if (!r.ok) throw new Error(url); return r; });
  };
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.dr-cartset__drop');
    if (!btn || busy) return;
    e.preventDefault();
    var row = btn.closest('.dr-cart-item, .dr-mini-cart__item');
    var holder = row && row.querySelector('[data-index]');
    if (!holder) return;
    var line = parseInt(holder.getAttribute('data-index'), 10);
    var drop = parseInt(btn.getAttribute('data-part'), 10), keep = 1 - drop;
    busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
      var it = cart.items[line - 1];
      var set = it && data.sets.filter(function (s) { return s.handle === it.handle; })[0];
      if (!set) throw new Error('noset');
      var o = it.options_with_values.map(function (x) { return x.value; });
      var v = set.pv[keep].filter(function (x) { return x.ok && x.o[0] === o[0] && x.o[1] === o[1 + keep]; })[0];
      if (!v) throw new Error('novariant');
      var li = btn.closest('.dr-cartset__row');
      if (li) window.__drCollapse(li);
      var wait = new Promise(function (res) { setTimeout(res, 460); });
      return post('/cart/add.js', { items: [{ id: v.id, quantity: 1 }] })
        .then(function () { return post('/cart/change.js', { id: it.key, quantity: it.quantity - 1 }); })
        .then(function () { return wait; })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:reload'));
          toast((data.split || '').replace('{title}', set.ptitle[drop] + ' · ' + o[0]));
        });
    }).catch(function () {
      document.querySelectorAll('.dr-cartset__row[data-collapsed]').forEach(function (r) { window.__drRestore(r); });
    }).then(function () { busy = false; });
  });
})();


/* product cards: the colour picked on the card is handed to quick view / quick add */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var sw = e.target.closest && e.target.closest('dr-swatch-card');
    if (!sw) return;
    var card = sw.closest('.dr-card-product');
    var id = sw.getAttribute('data-vrid');
    if (!card || !id) return;
    card.querySelectorAll('[aria-controls="dr-quick-view-modal"], [aria-controls="dr-quick-add-modal"]').forEach(function (b) {
      b.setAttribute('search-params', 'variant=' + id);
    });
  }, true);
})();

/* quick view: picking a colour fades the gallery, lands on that colour's first photo, fades back in */
(function () {
  'use strict';
  var timers = new WeakMap();
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio' || !input.closest) return;
    var modal = input.closest('#dr-quick-view-modal, #dr-quick-add-modal');
    if (!modal || !/farbe|color|colour|couleur|色/i.test(input.name || '')) return;
    var slider = modal.querySelector('dr-slider');
    if (!slider) return;
    clearTimeout(timers.get(slider));
    slider.classList.add('dr-swap-out');
    timers.set(slider, setTimeout(function () {
      if (slider.goToIndex) { try { slider.goToIndex(0, true); } catch (err) {} }
      setTimeout(function () { slider.classList.remove('dr-swap-out'); }, 30);
    }, 750));
  });
})();


/* never two side drawers on top of each other: the open one closes first, then the next one opens */
(function () {
  'use strict';
  var patch = function () {
    var ctor = customElements.get('dr-lazy-drawer') || customElements.get('dr-lazy-modal');
    if (!ctor) return false;
    var proto = ctor.prototype, base = null;
    while (proto && proto !== HTMLElement.prototype) {
      if (Object.prototype.hasOwnProperty.call(proto, 'animateDialogOpen') && Object.prototype.hasOwnProperty.call(proto, 'open')) base = proto;
      proto = Object.getPrototypeOf(proto);
    }
    if (!base || base.__drOne) return !!base;
    var orig = base.open;
    base.__drOne = true;
    base.open = async function () {
      var args = arguments, self = this;
      var others = Array.prototype.slice.call(document.querySelectorAll('dialog[open]')).filter(function (d) {
        var host = d.parentElement;
        return d !== self.dialog && host && typeof host.close === 'function' && host._openedAsModal && !/cookie/i.test(d.id + ' ' + d.className) && !self.contains(d) && !d.contains(self.dialog);
      });
      /* language and currency are the one exception: they may open right on top of the menu */
      var exempt = self.dialog && /^popover-(currencies|languages)$/.test(self.dialog.id);
      if (others.length && !exempt && !self.hasAttribute('nested')) {
        await Promise.all(others.map(function (d) { return d.parentElement.close(); }));
      }
      return orig.apply(self, args);
    };
    return true;
  };
  var tries = 0;
  var go = function () { if (!patch() && tries++ < 40) setTimeout(go, 250); };
  go();
})();


/* cart: change colour and size of any line right in the cart, and offer the set to single pieces */
(function () {
  'use strict';
  var el = document.getElementById('dr-set-map');
  var data = { sets: [] };
  try { if (el) data = JSON.parse(el.textContent); } catch (e) {}
  var busy = false;
  var ROWS = '.dr-cart-item, .dr-mini-cart__item';
  var prods = {};
  var post = function (url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then(function (r) { if (!r.ok) throw new Error(url); return r; });
  };
  var getCart = function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); };
  var lineOf = function (row) {
    var h = row && row.querySelector('[data-index]');
    return h ? parseInt(h.getAttribute('data-index'), 10) : 0;
  };
  var money = function (c) {
    try { return (c / 100).toLocaleString(document.documentElement.lang || 'de', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }); }
    catch (e) { return (c / 100).toFixed(2); }
  };
  var setOf = function (it) { return data.sets.filter(function (s) { return s.handle === it.handle; })[0]; };
  /* variants as {id, o:[...], ok} for sets (from the page) or any product (fetched once, kept) */
  var variantsFor = function (it) {
    var s = setOf(it);
    if (s) return Promise.resolve(s.variants);
    if (prods[it.handle]) return prods[it.handle];
    prods[it.handle] = fetch('/products/' + it.handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
      return p.variants.map(function (v) { return { id: v.id, o: [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }), ok: v.available }; });
    }).catch(function () { delete prods[it.handle]; return []; });
    return prods[it.handle];
  };
  var roundify = function (el) { if (el && el.textContent.trim().length <= 1) el.classList.add('dr-round'); };
  /* running "glide into place" animations of the lines around a closing menu */
  var flipAnims = [];
  var settleFlip = function () {
    return flipAnims.filter(function (a) { return a.playState === 'running'; }).map(function (a) {
      var t = a.currentTime;
      a.pause();
      a.currentTime = a.effect.getComputedTiming().endTime;
      return { a: a, t: t };
    });
  };
  var closeMenus = function (instant) {
    document.querySelectorAll('.dr-vmenu').forEach(function (m) {
      if (m.__closing) return;
      m.__closing = true;
      var chip = m.__chip;
      var pills = Array.prototype.slice.call(m.querySelectorAll('button'));
      var finish = function () {
        m.remove();
        /* a late timer must not touch a chip that has a newer menu open by now */
        if (chip && (!chip.__vm || chip.__vm === m)) { chip.classList.remove('is-open'); chip.hidden = false; }
      };
      if (instant || !pills.length || !m.animate || !chip || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) { finish(); return; }
      /* The layout settles to its final state at once (chip back in the flow, menu lifted out of it); everything that moves is then
         animated with transform/opacity only (FLIP), so nothing re-flows frame by frame and nothing stutters. */
      var dur = 340;
      var ease = 'cubic-bezier(.4,0,.2,1)';
      var scope = chip.closest(ROWS) || m.parentNode;
      var movers = [];
      scope.querySelectorAll('*').forEach(function (el) {
        /* the chip is hidden while the menu is open, so its insides have no position to glide from */
        if (m.contains(el) || chip.contains(el) || el.contains(m) || el.contains(chip)) return;
        var d = getComputedStyle(el).display;
        if (d === 'inline' || d === 'contents' || d === 'none') return;
        movers.push({ el: el, r: el.getBoundingClientRect() });
      });
      /* closing right after (or while) the options unfold: take over from where each pill is right now, not from its settled place */
      var now = pills.map(function (p) {
        var cs = getComputedStyle(p);
        return { tf: cs.transform === 'none' ? 'translate(0,0)' : cs.transform, op: cs.opacity, bg: cs.backgroundColor, col: cs.color, bd: cs.borderTopColor };
      });
      pills.forEach(function (p) { p.getAnimations().forEach(function (a) { a.cancel(); }); });
      var prs = pills.map(function (p) { return p.getBoundingClientRect(); });
      var mr = m.getBoundingClientRect();
      var c = Math.max(0, pills.findIndex(function (p) { return p.classList.contains('is-current'); }));
      var cur = pills[c];
      if (cur.classList.contains('dr-swatch-pick')) cur.classList.add('is-fading');
      var inr = m.querySelector('.dr-vmenu__in');
      if (inr) { inr.style.flex = '0 0 auto'; inr.style.minWidth = '0'; inr.style.width = mr.width + 'px'; inr.style.maxWidth = 'none'; }
      m.style.setProperty('grid-column', 'auto', 'important');
      m.style.setProperty('grid-row', 'auto', 'important');
      m.style.position = 'absolute';
      m.style.left = '0px';
      m.style.top = '0px';
      m.style.width = mr.width + 'px';
      m.style.height = mr.height + 'px';
      m.style.maxWidth = 'none';
      m.style.zIndex = '3';
      m.style.pointerEvents = 'none';
      /* place by measured position, not offsetLeft/Top: a transformed ancestor (the cart page rows) is the containing block without being the offsetParent */
      var z = m.getBoundingClientRect();
      m.style.left = (mr.left - z.left) + 'px';
      m.style.top = (mr.top - z.top) + 'px';
      chip.hidden = false;
      var cr = chip.getBoundingClientRect();
      var cpr = prs[c];
      /* a pill with the chip's own size (colour dot, single letter) just lands on it: no cross-fade, so nothing flickers */
      var same = Math.abs(cr.width - cpr.width) < 2 && Math.abs(cr.height - cpr.height) < 2;
      /* whatever shifted because the menu left the flow glides to its place */
      var shift = new Map();
      movers.forEach(function (o) {
        var n = o.el.getBoundingClientRect();
        shift.set(o.el, { x: o.r.left - n.left, y: o.r.top - n.top });
      });
      movers.forEach(function (o) {
        var s = shift.get(o.el), par = o.el.parentElement, ps = null;
        while (par && par !== scope.parentNode) { if (shift.has(par)) { ps = shift.get(par); break; } par = par.parentElement; }
        var rx = s.x - (ps ? ps.x : 0), ry = s.y - (ps ? ps.y : 0);
        if (Math.abs(rx) < .5 && Math.abs(ry) < .5) return;
        /* 'add' keeps a glide that is still running from an earlier close instead of replacing (= jumping) it */
        var fa = o.el.animate([{ transform: 'translate(' + rx + 'px,' + ry + 'px)' }, { transform: 'translate(0,0)' }], { duration: dur, easing: ease, composite: 'add' });
        flipAnims.push(fa);
        fa.onfinish = fa.oncancel = function () { var k = flipAnims.indexOf(fa); if (k >= 0) flipAnims.splice(k, 1); };
      });
      /* the chip fades in underneath while the chosen pill, now centred on it, fades out */
      chip.classList.remove('is-open');
      /* the chip stays out of sight until the options have left (it would show its colour under the first option); the pill on top hands over at the end */
      if (same) chip.animate([{ opacity: 0, offset: 0 }, { opacity: 0, offset: .98 }, { opacity: 1, offset: 1 }], { duration: dur, fill: 'backwards' });
      else chip.animate([{ opacity: 0 }, { opacity: 1 }], { duration: dur * .6, delay: dur * .4, easing: 'ease-out', fill: 'backwards' });
      /* a colour pill lands on the dot of a wide chip, not on its middle */
      var dotEl = chip.querySelector('.dr-vdot');
      var tr = dotEl ? dotEl.getBoundingClientRect() : cr;
      pills.forEach(function (p, i) {
        var pr = prs[i];
        var dx = tr.left + tr.width / 2 - (pr.left + pr.width / 2);
        var dy = tr.top + tr.height / 2 - (pr.top + pr.height / 2);
        if (i === c) {
          var keys = [
            { transform: now[i].tf, opacity: now[i].op, offset: 0 },
            { transform: 'translate(' + dx * .7 + 'px,' + dy * .7 + 'px)', opacity: 1, offset: .6 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px)', opacity: same ? 1 : 0, offset: 1 }
          ];
          if (!p.classList.contains('dr-swatch-pick')) {
            keys[0].backgroundColor = now[i].bg; keys[0].color = now[i].col; keys[0].borderColor = now[i].bd;
            keys[1].backgroundColor = 'rgba(122,68,70,0)'; keys[1].color = '#4a2c2a'; keys[1].borderColor = '#e3bdb9';
            keys[2].backgroundColor = 'rgba(122,68,70,0)'; keys[2].color = '#4a2c2a'; keys[2].borderColor = '#e3bdb9';
          }
          p.animate(keys, { duration: dur, easing: ease, fill: 'forwards' });
        } else {
          /* gone before they reach the chip: nothing is left crowding around it */
          p.animate([
            { transform: now[i].tf, opacity: now[i].op, offset: 0 },
            { transform: 'translate(' + dx * .6 + 'px,' + dy * .6 + 'px) scale(.75)', opacity: 0, offset: .55 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.6)', opacity: 0, offset: 1 }
          ], { duration: dur, easing: ease, fill: 'forwards' });
        }
      });
      setTimeout(finish, dur + 30);
    });
    document.querySelectorAll('.dr-vchip.is-open[hidden], .dr-cartset__size.is-open[hidden]').forEach(function (c) { if (instant) { c.classList.remove('is-open'); c.hidden = false; } });
  };
  window.__drCloseMenus = closeMenus;

  /* swap one variant for another in a single request, with the line gliding out and the new one in */
  var swap = function (row, it, newId) {
    busy = true;
    window.__drCollapse(row);
    var wait = new Promise(function (res) { setTimeout(res, 460); });
    var updates = {};
    updates[it.key] = 0;
    return getCart().then(function (cart) {
      var have = cart.items.filter(function (x) { return x.variant_id === newId; })[0];
      updates[newId] = it.quantity + (have ? have.quantity : 0);
      return post('/cart/update.js', { updates: updates });
    }).then(function () { return wait; }).then(function () {
      document.dispatchEvent(new CustomEvent('cart:reload'));
    }).catch(function () { window.__drRestore(row); }).then(function () { busy = false; });
  };

  var restoreFlip = null;
  var openMenu0 = function (chip) {
    var row = chip.closest(ROWS);
    var vi = row && row.__vi;
    if (!vi) return;
    var k = parseInt(chip.getAttribute('data-opt'), 10);
    var was = chip.classList.contains('is-open');
    /* opening again while this chip's own menu is still folding away: that old one goes at once, so two menus never overlap */
    document.querySelectorAll('.dr-vmenu').forEach(function (x) { if (x.__closing && x.__chip === chip) { x.remove(); } });
    closeMenus();
    /* measure on the settled layout: lines still gliding from the close above would offset every position */
    restoreFlip = settleFlip();
    if (was) return;
    var cur = vi.o;
    var values = [], avail = {};
    vi.vs.forEach(function (v) {
      var x = v.o[k];
      if (values.indexOf(x) < 0) values.push(x);
      if (v.ok) avail[x] = true;
    });
    if (values.indexOf(cur[k]) < 0) values.unshift(cur[k]);
    avail[cur[k]] = true;
    /* a single possible value: nothing to choose, so nothing opens */
    if (values.length <= 1) { chip.classList.add('is-static'); return; }
    var menu = document.createElement('span');
    menu.className = 'dr-vmenu';
    var inner = document.createElement('span');
    inner.className = 'dr-vmenu__in';
    menu.appendChild(inner);
    var colors = data.colors || {};
    var isColor = values.every(function (x) { return colors[x]; });
    if (isColor) menu.className += ' dr-vmenu--colors';
    values.forEach(function (val) {
      var b = document.createElement('button');
      b.type = 'button';
      if (isColor) {
        b.className = 'dr-swatch-pick';
        b.style.setProperty('--dr-bg-color', colors[val]);
        b.setAttribute('aria-label', val);
        b.title = val;
      } else {
        b.textContent = val;
        roundify(b);
      }
      b.setAttribute('data-val', val);
      if (val === cur[k]) b.className += ' is-current';
      if (!avail[val]) {
        b.className += ' is-soldout';
        b.setAttribute('aria-disabled', 'true');
        b.title = (b.title ? b.title + ' · ' : '') + (data.soldout || '');
      }
      b.addEventListener('click', function (ev) {
        ev.stopPropagation();
        if (!avail[val]) return;
        if (val === cur[k] || busy) { closeMenus(); return; }
        var best = null, bestScore = -1;
        vi.vs.forEach(function (v) {
          if (!v.ok || v.o[k] !== val) return;
          var score = 0;
          cur.forEach(function (x, i) { if (i !== k && v.o[i] === x) score++; });
          if (score > bestScore) { best = v; bestScore = score; }
        });
        closeMenus(true);
        if (best) swap(row, vi.it, best.id);
      });
      inner.appendChild(b);
    });
    var cr = chip.getBoundingClientRect();
    menu.style.minHeight = cr.height + 'px';
    /* many options in a narrow set row take a line of their own */
    var inSetRow = chip.closest('.dr-cartset__row') && !chip.closest('.dr-cartset--compact');
    if (inSetRow && values.length > 4) menu.setAttribute('data-wide', '1');
    chip.classList.add('is-open');
    chip.hidden = true;
    menu.__chip = chip;
    chip.__vm = menu;
    menu.__cw = cr.width;
    menu.__ch = cr.height;
    chip.insertAdjacentElement('afterend', menu);
    menu.classList.add('is-in');
    var pills = Array.prototype.slice.call(menu.querySelectorAll('button'));
    var ci = Math.max(0, pills.findIndex(function (p) { return p.classList.contains('is-current'); }));
    if (pills.length > 1 && menu.animate) {
      var mr = menu.getBoundingClientRect();
      var ease = 'cubic-bezier(.22,1,.36,1)';
      pills.forEach(function (p, i) {
        var pr = p.getBoundingClientRect();
        /* every pill starts stacked on the chip, wherever it ends up, also on a second line */
        var dx = cr.left + (cr.width - pr.width) / 2 - pr.left;
        var dy = cr.top + (cr.height - pr.height) / 2 - pr.top;
        p.style.zIndex = i === ci ? 2 : 1;
        if (i === ci && !p.classList.contains('dr-swatch-pick')) {
          p.animate([
            { backgroundColor: 'rgba(122,68,70,0)', color: '#4a2c2a', borderColor: '#e3bdb9' },
            { backgroundColor: 'rgba(122,68,70,1)', color: '#ffffff', borderColor: '#7a4446' }
          ], { duration: 420, easing: 'ease-out', fill: 'backwards' });
        }
        p.animate([
          { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + (i === ci ? 1 : .6) + ')', opacity: i === ci ? 1 : 0 },
          { transform: 'translate(0,0) scale(1)', opacity: 1 }
        ], { duration: 460, easing: ease, fill: 'backwards' });
      });
      /* a second line makes room smoothly instead of pushing the rest down in one jump */
      if (mr.height > cr.height + 2) {
        menu.animate([{ height: cr.height + 'px' }, { height: mr.height + 'px' }], { duration: 460, easing: ease });
      }
    }
  };

  var openMenu = function (chip) {
    restoreFlip = null;
    try { openMenu0(chip); } finally {
      (restoreFlip || []).forEach(function (x) { x.a.currentTime = x.t; x.a.play(); });
      restoreFlip = null;
    }
  };

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('.dr-vchip, .dr-cartset__size');
    if (chip) { e.preventDefault(); if (!busy) openMenu(chip); return; }
    if (!(e.target.closest && e.target.closest('.dr-vmenu'))) closeMenus();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeMenus(); });

  var dot = function (chip, val) {
    var c = (data.colors || {})[val];
    if (!c) return;
    var d = document.createElement('i');
    d.className = 'dr-vdot';
    d.style.setProperty('--dr-bg-color', c);
    chip.insertBefore(d, chip.firstChild);
  };
  /* build the chips for a line once its data is known */
  /* an option with a single possible value has nothing to choose: no arrow, no menu, from the start */
  var markStatic = function (row) {
    var vi = row.__vi;
    if (!vi) return;
    row.querySelectorAll('.dr-vchip, .dr-cartset__size').forEach(function (c) {
      var k = parseInt(c.getAttribute('data-opt'), 10);
      if (isNaN(k)) return;
      var vals = [];
      vi.vs.forEach(function (v) { if (vals.indexOf(v.o[k]) < 0) vals.push(v.o[k]); });
      if (vals.indexOf(vi.o[k]) < 0) vals.push(vi.o[k]);
      c.classList.toggle('is-static', vals.length <= 1);
    });
  };
  var enhance = function (row, it, vs) {
    row.__vi = { it: it, vs: vs, o: it.options_with_values.map(function (x) { return x.value; }) };
    setTimeout(function () { markStatic(row); }, 0);
    var s = setOf(it);
    if (s) {
      row.querySelectorAll('.dr-cartset__size').forEach(function (c) { c.setAttribute('data-opt', 1 + parseInt(c.getAttribute('data-part'), 10)); c.classList.add('dr-vchip'); roundify(c); });
      var strong = row.querySelector('.dr-cartset__color strong');
      if (strong && !strong.querySelector('.dr-vchip')) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'dr-vchip'; b.setAttribute('data-opt', '0'); b.textContent = strong.textContent; dot(b, strong.textContent);
        strong.textContent = ''; strong.appendChild(b);
      }
      return;
    }
    if (it.product_has_only_default_variant || !it.options_with_values.length || (it.options_with_values.length === 1 && /default|title/i.test(it.options_with_values[0].name))) return;
    var title = row.querySelector('.dr-page-cart__title-wrapper, .dr-mini-cart__title-wrapper');
    if (!title || row.querySelector('.dr-vedit')) return;
    var box = document.createElement('div');
    box.className = 'dr-vedit';
    it.options_with_values.forEach(function (opt, i) {
      var r = document.createElement('span');
      r.className = 'dr-vedit__row';
      var l = document.createElement('span');
      l.className = 'dr-vedit__lbl sr-only'; l.textContent = ((data.names || {})[it.handle] || [])[i] || opt.name;
      var c = document.createElement('button');
      c.type = 'button'; c.className = 'dr-vchip'; c.setAttribute('data-opt', i); if ((data.colors || {})[opt.value]) { c.classList.add('dr-vchip--color', 'dr-round'); c.setAttribute('title', opt.value); c.setAttribute('aria-label', opt.name + ': ' + opt.value); dot(c, opt.value); } else { c.textContent = opt.value; roundify(c); }
      r.appendChild(l); r.appendChild(c); box.appendChild(r);
    });
    title.insertAdjacentElement('afterend', box);
  };

  /* upgrade hint on single pieces */
  var upgrade = function (row, it) {
    var set = data.sets.filter(function (s) { return s.parts.indexOf(it.handle) >= 0; })[0];
    if (!set) return;
    var idx = set.parts.indexOf(it.handle), other = 1 - idx;
    var color = it.options_with_values[0].value, size = it.options_with_values[1] && it.options_with_values[1].value;
    var pv = set.pv[other].filter(function (x) { return x.ok && x.o[0] === color; });
    if (!pv.length) return;
    var pick = pv.filter(function (x) { return x.o[1] === size; })[0] || pv[0];
    var sv = set.variants.filter(function (s) { return s.ok && s.o[0] === color && s.o[1 + idx] === size && s.o[1 + other] === pick.o[1]; })[0];
    if (!sv) return;
    var saved = (it.final_price + pick.price - sv.price) * it.quantity;
    if (saved <= 0) return;
    var host = row.querySelector('.dr-page-cart__info') || (row.querySelector('.dr-mini-cart__title-wrapper') || {}).parentElement;
    if (!host || host.querySelector('.dr-upgrade:not(.dr-upgrade-hold)')) return;
    var hold = host.querySelector('.dr-upgrade-hold');
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'dr-upgrade';
    b.textContent = (data.upgrade || '').replace('{saved}', money(saved));
    b.addEventListener('click', function () {
      if (busy) return;
      busy = true;
      b.disabled = true;
      post('/cart/add.js', { items: [{ id: pick.id, quantity: it.quantity }] }).then(function () { busy = false; return window.__drMerge ? window.__drMerge() : false; }).catch(function () { busy = false; b.disabled = false; });
    });
    if (hold) {
      // same line re-rendered: no unfold, only a soft fade if the amount changed
      b.classList.add('dr-upgrade--still');
      var changedText = hold.textContent !== b.textContent;
      hold.replaceWith(b);
      if (changedText && b.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) b.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.22,.61,.36,1)' });
    } else host.appendChild(b);
  };

  var annotate = function () {
    var rows = Array.prototype.filter.call(document.querySelectorAll(ROWS), function (r) { return !r.hasAttribute('data-dr-up') && r.querySelector('[data-index]'); });
    if (!rows.length) return;
    getCart().then(function (cart) {
      rows.forEach(function (row) {
        row.setAttribute('data-dr-up', '1');
        var it = cart.items[lineOf(row) - 1];
        var dropHold = function () { var h = row.querySelector('.dr-upgrade-hold'); if (h) h.remove(); };
        if (!it) return dropHold();
        variantsFor(it).then(function (vs) { enhance(row, it, vs); });
        upgrade(row, it);
        dropHold();
      });
    }).catch(function () {});
  };
  var t;
  var sched = function () { clearTimeout(t); t = setTimeout(annotate, 250); };
  new MutationObserver(sched).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sched); else sched();
})();

/* images in cart, drawer, wishlist: placeholder while loading, then a gentle fade */
(function () {
  'use strict';
  var SEL = '.dr-cart-item img, .dr-mini-cart__item img, .dr-wishlist-grid img, .dr-cartset__thumb img, .dr-page-cart__image img';
  var loadedSrc = {};
  var prep = function (img) {
    if (img.__drp) return;
    img.__drp = 1;
    // keyed by file path without size/version query: a re-rendered cart line asks for the same file (sometimes another width) and must not blink back to the placeholder
    var key = function (u) { return (u || '').split('?')[0]; };
    var known = loadedSrc[key(img.currentSrc)] || loadedSrc[key(img.src)];
    if ((img.complete && img.naturalWidth > 0) || known) { loadedSrc[key(img.currentSrc || img.src)] = 1; loadedSrc[key(img.src)] = 1; return; }
    var parent = img.parentElement;
    img.classList.add('dr-img-prep', 'dr-img-loading');
    if (parent) parent.classList.add('dr-skel');
    var done = function () {
      loadedSrc[key(img.currentSrc || img.src)] = 1;
      loadedSrc[key(img.src)] = 1;
      img.classList.remove('dr-img-loading');
      if (parent) parent.classList.remove('dr-skel');
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    setTimeout(done, 12000);
  };
  var scan = function () { document.querySelectorAll(SEL).forEach(prep); };
  var t;
  new MutationObserver(function () { clearTimeout(t); t = setTimeout(scan, 30); }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();

/* cart rows and drawer rows glide in one after the other */
(function () {
  'use strict';
  var stagger = function (root) {
    root.querySelectorAll('.dr-cart-item, .dr-mini-cart__item').forEach(function (r, i) {
      if (r.classList.contains('dr-row-in')) return;
      r.style.setProperty('--dr-d', Math.min(i, 6) * 80 + 'ms');
      r.classList.remove('dr-stagger');
      void r.offsetWidth;
      r.classList.add('dr-stagger');
      setTimeout(function () { r.classList.remove('dr-stagger'); r.style.removeProperty('--dr-d'); }, 1400);
    });
  };
  var wire = function () {
    var d = document.getElementById('CartDrawer');
    if (d && !d.__drs) {
      d.__drs = 1;
      new MutationObserver(function () { if (d.hasAttribute('open')) stagger(d); else if (window.__drCloseMenus) window.__drCloseMenus(true); }).observe(d, { attributes: true, attributeFilter: ['open'] });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire); else wire();
})();


/* values that change glide instead of snapping: prices, totals, labels, counters; hearts pop; new cards stagger in */
(function () {
  'use strict';
  var loadedAt = Date.now();
  var VALUE = '.dr-product__price, .dr-totals__total-value, .dr-mini-cart__price, .dr-page_cart__total-price, .dr-product-form__label, dr-cart-count, dr-wishlist-count, .dr-cartset__save-inline';
  var replay = function (el, cls) {
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    setTimeout(function () { el.classList.remove(cls); }, 700);
  };
  var seenText = new WeakMap();
  var pending = new Set();
  var flush = function () {
    pending.forEach(function (el) {
      var txt = el.textContent.replace(/\s+/g, ' ').trim();
      var prev = seenText.get(el);
      seenText.set(el, txt);
      if (prev === undefined || prev === txt) return;
      if (el.matches('.dr-totals__total-value') && el.closest('dr-main-cart')) return; // the cart page rolls this value itself (global.min.js)
      replay(el, /count/i.test(el.tagName + el.className) ? 'dr-bump' : 'dr-swap');
    });
    pending.clear();
  };
  var timer;
  var primeValues = function () { document.querySelectorAll(VALUE).forEach(function (el) { if (!seenText.has(el)) seenText.set(el, el.textContent.replace(/\s+/g, ' ').trim()); }); };
  primeValues();
  var mo = new MutationObserver(function (list) {
    var cards = [];
    list.forEach(function (m) {
      if (m.type === 'attributes') {
        if (m.target.tagName === 'DR-CORE-WISHLIST' && m.attributeName === 'action') replay(m.target, 'dr-pop');
        return;
      }
      var t = m.target.nodeType === 3 ? m.target.parentElement : m.target;
      var host = t && t.closest && t.closest(VALUE);
      if (host) pending.add(host);
      m.addedNodes && m.addedNodes.forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches(VALUE)) pending.add(n);
        if (n.querySelectorAll) n.querySelectorAll(VALUE).forEach(function (x) { if (!seenText.has(x)) seenText.set(x, x.textContent.replace(/\s+/g, ' ').trim()); });
        if (n.matches && n.matches('.dr-card-product') && Date.now() - loadedAt > 2000) cards.push(n);
      });
    });
    if (pending.size) { clearTimeout(timer); timer = setTimeout(flush, 60); }
    cards.forEach(function (n, i) {
      n.style.setProperty('--dr-d', Math.min(i, 8) * 70 + 'ms');
      n.classList.add('dr-stagger');
      setTimeout(function () { n.classList.remove('dr-stagger'); n.style.removeProperty('--dr-d'); }, 1500);
    });
  });
  mo.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['action'] });
})();

/* footer hairline takes the width of the script line above it */
(function () {
  'use strict';
  var set = function () {
    var s = document.querySelector('.dr-statement__script');
    var f = document.querySelector('.dr-footer-section');
    if (!f) return;
    if (s) f.style.setProperty('--dr-ft-line', Math.round(s.getBoundingClientRect().width + 190) + 'px');
    else f.style.removeProperty('--dr-ft-line');
  };
  var go = function () { set(); if (document.fonts && document.fonts.ready) document.fonts.ready.then(set); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
  window.addEventListener('load', set);
  window.addEventListener('resize', set);
})();


/* rows and parts fold up softly instead of just vanishing, and unfold when restored */
(function () {
  'use strict';
  window.__drCollapse = function (row) {
    if (!row || row.hasAttribute('data-collapsed')) return;
    var cs = getComputedStyle(row);
    row.__drs = row.getAttribute('style') || '';
    row.setAttribute('data-collapsed', '1');
    row.style.height = row.offsetHeight + 'px';
    row.style.overflow = 'hidden';
    row.style.transition = 'height .5s cubic-bezier(.22,1,.36,1), opacity .35s ease, padding .5s cubic-bezier(.22,1,.36,1), margin .5s cubic-bezier(.22,1,.36,1), border-width .5s ease';
    void row.offsetHeight;
    row.style.height = '0px';
    row.style.opacity = '0';
    row.style.paddingTop = '0px';
    row.style.paddingBottom = '0px';
    row.style.marginTop = '0px';
    row.style.marginBottom = '0px';
    row.style.borderWidth = '0px';
  };
  window.__drRestore = function (row) {
    if (!row || !row.hasAttribute('data-collapsed')) return;
    row.removeAttribute('data-collapsed');
    if (row.__drs) row.setAttribute('style', row.__drs); else row.removeAttribute('style');
  };
})();

/* removing a line: it folds up right away while the cart updates */
document.addEventListener('click', function (e) {
  var a = e.target.closest && e.target.closest('wrapp-remove-item-oncart, .dr-mini-cart__remove');
  if (!a) return;
  var row = a.closest('.dr-cart-item, .dr-mini-cart__item');
  if (row && window.__drCollapse) window.__drCollapse(row);
}, true);

/* one calm tooltip for everything that is only an icon or a colour swatch (also readable without colour vision) */
(function () {
  'use strict';
  var SEL = '.dr-vchip--color, .dr-swatch-pick, dr-swatch-card, label.dr-product-form_value.is-type-color, .dr-cartset__drop, .dr-mini-cart__remove';
  var textOf = function (el) {
    if (el.getAttribute('data-tip')) return el.getAttribute('data-tip');
    var n = el.querySelector && el.querySelector('.dr-form-color-name');
    var t = (n && n.textContent) || el.getAttribute('title') || el.getAttribute('aria-label') || (el.querySelector && (el.querySelector('.sr-only') || {}).textContent) || '';
    return t.replace(/^[^:]*:\s*/, function (m) { return /Farbe|Color|Größe|Size/i.test(m) ? '' : m; }).trim();
  };
  var prep = function (root) {
    (root.querySelectorAll ? root.querySelectorAll(SEL) : []).forEach(function (el) {
      if (el.__tipd) return;
      /* elements that already carry the theme's own tooltip keep only that one */
      var own = el.closest('dr-tooltip, wrapp-remove-item-oncart');
      if (own && (own.getAttribute('placement') || 'bottom') !== 'none') { el.__tipd = 1; return; }
      var t = textOf(el);
      if (!t) return;
      el.__tipd = 1;
      el.setAttribute('data-tip', t);
      /* a theme tooltip inside the element (colour swatches) stays silent, so only one tooltip shows */
      el.querySelectorAll('dr-tooltip').forEach(function (inner) { inner.setAttribute('will-hidden-txt', ''); });
      if (el.hasAttribute('title')) el.removeAttribute('title');
    });
  };
  var tip, timer, cur;
  var ensure = function () {
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'dr-tip';
    tip.setAttribute('popover', 'manual');
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
    return tip;
  };
  var hide = function () {
    clearTimeout(timer);
    cur = null;
    if (tip) { tip.classList.remove('is-in'); try { tip.hidePopover(); } catch (e) {} }
  };
  var show = function (el) {
    var t = el.getAttribute('data-tip');
    if (!t) return;
    ensure();
    tip.textContent = t;
    try { tip.showPopover(); } catch (e) {}
    var r = el.getBoundingClientRect();
    var w = tip.offsetWidth, h = tip.offsetHeight;
    var left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2));
    var top = r.top - h - 10;
    if (top < 8) { top = r.bottom + 10; tip.classList.add('is-below'); } else tip.classList.remove('is-below');
    tip.style.left = left + 'px';
    tip.style.top = top + 'px';
    tip.style.setProperty('--dr-tip-x', (r.left + r.width / 2 - left) + 'px');
    tip.classList.add('is-in');
    cur = el;
  };
  document.addEventListener('mouseover', function (e) {
    var el = e.target.closest && e.target.closest('[data-tip]');
    if (!el || el === cur) return;
    clearTimeout(timer);
    timer = setTimeout(function () { show(el); }, 90);
  });
  document.addEventListener('mouseout', function (e) {
    var el = e.target.closest && e.target.closest('[data-tip]');
    if (el && !el.contains(e.relatedTarget)) hide();
  });
  document.addEventListener('focusin', function (e) { var el = e.target.closest && e.target.closest('[data-tip]'); if (el && e.target.matches(':focus-visible')) show(el); });
  document.addEventListener('focusout', hide);
  document.addEventListener('click', hide, true);
  window.addEventListener('scroll', hide, true);
  var t2;
  new MutationObserver(function () { clearTimeout(t2); t2 = setTimeout(function () { prep(document); }, 60); }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { prep(document); }); else prep(document);
})();


/* drawer tools (note, shipping, discount, gift): open as a panel inside the drawer instead of a second overlay */
(function () {
  'use strict';
  var setup = function () {
    document.querySelectorAll('#CartDrawer').forEach(function (drawer) {
      var tool = drawer.querySelector('.dr-mini-cart__tool');
      if (!tool || tool.__dt) return;
      var dialogs = drawer.querySelectorAll('dr-cart-action > dialog');
      if (!dialogs.length) return;
      tool.__dt = 1;
      var wrap = document.createElement('div');
      wrap.className = 'dr-tool-panels';
      tool.insertAdjacentElement('afterend', wrap);
      dialogs.forEach(function (d) {
        var content = d.firstElementChild;
        if (!content) return;
        var panel = document.createElement('div');
        panel.className = 'dr-tool-panel';
        panel.setAttribute('data-for', d.id);
        var inner = document.createElement('div');
        inner.className = 'dr-tool-panel__in';
        inner.appendChild(content);
        panel.appendChild(inner);
        wrap.appendChild(panel);
      });
    });
  };
  var closeAll = function (except) {
    document.querySelectorAll('.dr-tool-panel.is-open').forEach(function (p) { if (p !== except) p.classList.remove('is-open'); });
    document.querySelectorAll('.dr-mini-cart__tool_btn.is-active').forEach(function (b) {
      var id = b.getAttribute('aria-controls');
      if (!except || except.getAttribute('data-for') !== id) { b.classList.remove('is-active'); b.setAttribute('aria-expanded', 'false'); }
    });
  };
  var toggle = function (id) {
    var panel = document.querySelector('.dr-tool-panel[data-for="' + id + '"]');
    if (!panel) return false;
    var open = !panel.classList.contains('is-open');
    closeAll(open ? panel : null);
    panel.classList.toggle('is-open', open);
    document.querySelectorAll('.dr-mini-cart__tool_btn[aria-controls="' + id + '"]').forEach(function (b) {
      b.classList.toggle('is-active', open);
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    if (open) {
      setTimeout(function () {
        var f = panel.querySelector('textarea, input[type="text"], select');
        if (f && !('ontouchstart' in window)) { try { f.focus({ preventScroll: true }); } catch (e) {} }
        var sc = panel.closest('.dr-mini-cart__sroll') || panel.closest('[data-dr-scroll-me]');
        if (sc && panel.getBoundingClientRect().bottom > sc.getBoundingClientRect().bottom) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 380);
    }
    return true;
  };
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('#CartDrawer [aria-controls^="dialog-is--"], .dr-page-tools [aria-controls^="dialog-is--"]');
    if (!b) return;
    var id = b.getAttribute('aria-controls');
    if (!document.querySelector('.dr-tool-panel[data-for="' + id + '"]')) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    toggle(id);
  }, true);
  /* a click anywhere else folds the open panel back, Esc too */
  var fold = function () {
    if (!document.querySelector('.dr-tool-panel.is-open')) return;
    closeAll(null);
  };
  document.addEventListener('click', function (e) {
    if (!document.querySelector('.dr-tool-panel.is-open')) return;
    if (e.target.closest && e.target.closest('.dr-tool-panel, .dr-mini-cart__tool_btn, .dr-tip')) return;
    fold();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') fold(); });
  var t;
  new MutationObserver(function () { clearTimeout(t); t = setTimeout(setup, 80); }).observe(document.documentElement, { childList: true, subtree: true });
  var dr = document.getElementById('CartDrawer');
  if (dr) new MutationObserver(function () { if (!dr.hasAttribute('open')) fold(); }).observe(dr, { attributes: true, attributeFilter: ['open'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
})();

/* search: our own "clear" text button instead of the browser's cross */
(() => {
  const label = { de: 'Löschen', en: 'Clear', es: 'Borrar', fr: 'Effacer', ja: '消去' };
  const enhance = (input) => {
    if (input.dataset.drClear) return;
    const box = input.closest('.dr-search-inner');
    if (!box) return;
    input.dataset.drClear = '1';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dr-search-clear';
    btn.setAttribute('aria-label', label[(document.documentElement.lang || 'en').slice(0, 2)] || label.en);
    const submit = box.querySelector('.dr-mini-search__submit');
    box.insertBefore(btn, submit || null);
    const sync = () => box.classList.toggle('has-value', input.value !== '');
    input.addEventListener('input', sync);
    btn.addEventListener('click', () => {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      sync();
      input.focus();
    });
    sync();
  };
  const scan = () => document.querySelectorAll('input[data-input-search]').forEach(enhance);
  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();

/* mobile menu photos: load as soon as the menu is requested so the band never shows an empty placeholder */
document.addEventListener('click', (e) => {
  if (!e.target.closest || !e.target.closest('[aria-controls="menu_mobile"]')) return;
  document.querySelectorAll('#menu_mobile .dr-menu__img img.dr-menu__img-m[loading="lazy"]').forEach((i) => { i.loading = 'eager'; });
}, true);

/* help and legal pages: lines typed as "• text" become a real list, so they get the bow marker */
(() => {
  const run = () => {
    document.querySelectorAll('.dr-rte p, .dr-hgroup__body p, .dr-acc__body p').forEach((p) => {
      if (p.dataset.drList || p.closest('table, .dr-toc, nav')) return;
      const parts = p.innerHTML.split(/(?:<br\s*\/?>\s*)+/i).map((x) => x.trim()).filter(Boolean);
      const isItem = (x) => /^(•|·|–|-\s)\s*/.test(x.replace(/^<[^>]+>/, '').trim()) || /^•/.test(x);
      if (!parts.some(isItem)) return;
      const frag = document.createDocumentFragment();
      let ul = null;
      let intro = [];
      const flush = () => {
        if (intro.length) { const q = document.createElement('p'); q.innerHTML = intro.join('<br><br>'); q.dataset.drList = '1'; frag.appendChild(q); intro = []; }
      };
      parts.forEach((x) => {
        if (isItem(x)) {
          flush();
          if (!ul) { ul = document.createElement('ul'); frag.appendChild(ul); }
          const li = document.createElement('li');
          li.innerHTML = x.replace(/^(•|·|–|-)\s*/, '');
          ul.appendChild(li);
        } else {
          ul = null;
          intro.push(x);
        }
      });
      flush();
      p.replaceWith(frag);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

/* footer accordions (phones): opening one closes the others */
document.addEventListener('click', (e) => {
  if (window.innerWidth >= 768 || !e.target.closest) return;
  const head = e.target.closest('.dr-footer-section .dr-footer-heading');
  if (!head) return;
  const mine = head.parentElement;
  document.querySelectorAll('.dr-footer-section .dr-footer-heading').forEach((h) => {
    const box = h.parentElement;
    if (box === mine || !box.classList.contains('open')) return;
    box.classList.remove('open');
    const c = box.querySelector('.dr-collapse-content');
    if (c) c.style.height = '0';
  });
}, true);

/* product page accordions: opening one closes the others */
document.addEventListener('toggle', (e) => {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open || !d.classList.contains('dr-acc__item')) return;
  const wrap = d.closest('.dr-acc');
  if (!wrap) return;
  wrap.querySelectorAll('details.dr-acc__item[open]').forEach((o) => { if (o !== d) o.open = false; });
}, true);

/* sort and language dropdowns: wrap each option's label so the hover underline can sit under the text only */
(() => {
  const wrap = () => {
    document.querySelectorAll('.dr-popover-sort-list [role="option"], .dr-popover-language [role="option"]').forEach((b) => {
      if (b.querySelector('.dr-opt-t')) return;
      const t = b.textContent.trim();
      if (!t) return;
      b.textContent = '';
      const s = document.createElement('span');
      s.className = 'dr-opt-t';
      s.textContent = t;
      b.appendChild(s);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wrap); else wrap();
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('.dr-control-sorting')) setTimeout(wrap, 0); }, true);
})();

/* the space between the page content and the slogan (or the footer where there is no slogan) is the same on every page */
(() => {
  const target = () => (window.innerWidth < 768 ? 180 : window.innerWidth < 1150 ? 204 : 224);
  const ownContent = (e, cs) => [...e.childNodes].some((n) => n.nodeType === 3 && n.textContent.trim())
    || /^(IMG|PICTURE|SVG|VIDEO|INPUT|BUTTON|CANVAS|IFRAME|SELECT|TEXTAREA)$/i.test(e.tagName);
  // a sticky box (the cart summary) still takes its place in the layout: count the end of its container, not its stuck position
  let stickyBottom = 0;
  const walk = (root, fn) => {
    const tw = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (e) => {
        if (e.closest('dialog, template')) return NodeFilter.FILTER_REJECT;
        const cs = getComputedStyle(e);
        if (cs.position === 'sticky' && cs.display !== 'none' && e.parentElement) {
          const pcs = getComputedStyle(e.parentElement);
          stickyBottom = Math.max(stickyBottom, e.parentElement.getBoundingClientRect().bottom - (parseFloat(pcs.paddingBottom) || 0));
        }
        if (cs.display === 'none' || cs.position === 'fixed' || cs.position === 'sticky') return NodeFilter.FILTER_REJECT;
        if (cs.visibility === 'hidden') return NodeFilter.FILTER_SKIP;
        fn(e, cs);
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    while (tw.nextNode()) { /* fn runs in acceptNode */ }
  };
  const run = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;
    const sec = [...document.querySelectorAll('.shopify-section-group-footer-group')].find((s) => getComputedStyle(s).display !== 'none' && s.offsetHeight > 0);
    if (!sec) return;
    sec.style.marginTop = '';
    const parts = [...main.children].filter((s) => getComputedStyle(s).display !== 'none' && s.offsetHeight > 0).slice(-2);
    let bottom = 0;
    stickyBottom = 0;
    parts.forEach((p) => walk(p, (e, cs) => {
      if (!ownContent(e, cs) && !(cs.borderBottomStyle !== 'none' && cs.borderBottomWidth !== '0px')) return;
      const r = e.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      bottom = Math.max(bottom, r.bottom);
    }));
    bottom = Math.max(bottom, stickyBottom);
    let top = Infinity;
    walk(sec, (e, cs) => {
      if (!ownContent(e, cs) && !(cs.borderTopStyle !== 'none' && cs.borderTopWidth !== '0px')) return;
      const r = e.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      top = Math.min(top, r.top);
    });
    if (!bottom || top === Infinity) return;
    const delta = Math.round(target() - (top - bottom));
    const minDelta = Math.ceil(bottom - sec.getBoundingClientRect().top);
    const base = parseFloat(getComputedStyle(sec).marginTop) || 0;
    sec.style.marginTop = (base + Math.max(delta, minDelta)) + 'px';
  };
  let t = null;
  const queue = () => { clearTimeout(t); t = setTimeout(run, 200); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queue); else queue();
  window.addEventListener('load', queue);
  window.addEventListener('resize', queue);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
  const m = document.getElementById('MainContent');
  // cart page: while the summary panels open or close the slogan follows every frame, so it glides with the card instead of jumping afterwards
  let raf = 0;
  const live = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; run(); }); queue(); };
  if (m && 'ResizeObserver' in window) new ResizeObserver(document.querySelector('dr-main-cart') ? live : queue).observe(m);
})();

/* language and currency lists: opened from the main menu on larger screens they take the width of the menu panel */
document.addEventListener('click', (e) => {
  const t = e.target.closest && e.target.closest('[aria-controls="popover-currencies"], [aria-controls="popover-languages"]');
  if (!t) return;
  const dlg = document.getElementById(t.getAttribute('aria-controls'));
  if (!dlg) return;
  const panel = t.closest('#menu_mobile') && document.querySelector('#menu_mobile .dr-dialog-drawer__inner');
  if (panel && window.innerWidth >= 768) {
    dlg.style.setProperty('--dr-pop-w', Math.round(panel.getBoundingClientRect().width) + 'px');
    dlg.classList.add('dr-pop-in-menu');
  } else {
    dlg.classList.remove('dr-pop-in-menu');
  }
}, true);

/* a few chosen titles mix two typefaces: the first word in Bodoni italic, the rest in our script (single-word titles stay as they are) */
(() => {
  const SEL = '[id*="__tabs_collection"] h2.dr-heading-block, .dr-pairs__heading, .dr-recently-products .dr-heading-block';
  const mix = (el) => {
    if (el.dataset.drMix) return;
    let t = el;
    while (t.children.length === 1 && !t.firstElementChild.matches('br') && t.textContent.trim() === t.firstElementChild.textContent.trim()) t = t.firstElementChild;
    if (t.children.length) return;
    const text = t.textContent.replace(/\s+/g, ' ').trim();
    const words = text.split(' ');
    if (words.length < 2) return;
    let n = 1;
    while (n < words.length - 1 && /^[&+–—-]$/.test(words[n])) n++;
    const first = words.slice(0, n).join(' ');
    const rest = words.slice(n).join(' ');
    const serif = /Bodoni/i.test(getComputedStyle(t).fontFamily);
    el.dataset.drMix = '1';
    t.textContent = '';
    const a = document.createElement('span');
    a.className = 'dr-mix__a';
    a.textContent = first;
    const b = document.createElement('span');
    b.className = 'dr-mix__b';
    b.textContent = rest;
    t.classList.add('dr-mix', serif ? 'dr-mix--serif' : 'dr-mix--sans');
    t.append(a, document.createTextNode(' '), b);
  };
  const run = (root) => (root.querySelectorAll ? root.querySelectorAll(SEL) : []).forEach(mix);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document)); else run(document);
  new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.matches && n.matches(SEL)) mix(n); run(n); } }))).observe(document.documentElement, { childList: true, subtree: true });
})();

/* cookie banner: consent by category (Shopify Customer Privacy API). The card unfolds from the short notice into the categories;
   the footer link "Cookie settings" (href …#cookie-settings) opens it straight in that view. Without the banner the link opens the privacy page. */
(() => {
  const CATS = ['preferences', 'analytics', 'marketing'];
  const api = (cb) => {
    if (window.Shopify && window.Shopify.customerPrivacy) return cb();
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err) cb(); });
  };
  const parts = () => {
    const bar = document.querySelector('sys-cookies');
    const card = bar && bar.querySelector('.dr-drawer-cookie');
    return { bar, card };
  };
  const fill = (card) => {
    let cur = {};
    try { cur = window.Shopify.customerPrivacy.currentVisitorConsent() || {}; } catch (e) { /* not loaded yet */ }
    CATS.forEach((c) => { const i = card.querySelector(`[data-dr-ck="${c}"]`); if (i) i.checked = cur[c] === 'yes'; });
  };
  const setView = (card, prefs) => {
    card.classList.toggle('is-prefs', prefs);
    const o = card.querySelector('[data-dr-ck-open]');
    if (o) o.setAttribute('aria-expanded', prefs ? 'true' : 'false');
    const p = card.querySelector('.dr-ck__prefs');
    if (p) p.setAttribute('aria-hidden', prefs ? 'false' : 'true');
    if (prefs) fill(card);
    if (p) requestAnimationFrame(() => fade(p.querySelector('.dr-ck__panel')));
  };
  /* a soft fade at the bottom while the list can still scroll */
  const fade = (el) => {
    if (!el) return;
    const more = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
    el.classList.toggle('is-more', more);
  };
  document.addEventListener('scroll', (e) => { if (e.target.classList && e.target.classList.contains('dr-ck__panel')) fade(e.target); }, true);
  window.addEventListener('resize', () => fade(document.querySelector('.dr-drawer-cookie.is-prefs .dr-ck__panel')));
  const save = (bar, card, all) => {
    const v = {};
    CATS.forEach((c) => { const i = card.querySelector(`[data-dr-ck="${c}"]`); v[c] = all ? true : !!(i && i.checked); });
    v.sale_of_data = v.marketing;
    if (all) CATS.forEach((c) => { const i = card.querySelector(`[data-dr-ck="${c}"]`); if (i) i.checked = true; });
    api(() => {
      const done = () => {
        if (bar.cookies && bar.cookies.show == '1' && typeof window.setCookie === 'function') window.setCookie(bar.cookies.cookiesName, 'accepted', parseInt(bar.cookies.day_next, 10));
        bar.hideCookiesBanner();
        setTimeout(() => setView(card, false), 600);
      };
      window.Shopify.customerPrivacy.setTrackingConsent(v, done);
    });
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-dr-ck-open], [data-dr-ck-back], [data-dr-ck-save], [data-dr-ck-all], a[href*="#cookie-settings"]');
    if (!t) return;
    const { bar, card } = parts();
    if (!bar || !card || typeof bar.showCookiesBanner !== 'function') return;
    e.preventDefault();
    if (t.matches('[data-dr-ck-open]')) api(() => { setView(card, true); setTimeout(() => fade(card.querySelector('.dr-ck__panel')), 700); });
    else if (t.matches('[data-dr-ck-back]')) setView(card, false);
    else if (t.matches('[data-dr-ck-save]')) save(bar, card, false);
    else if (t.matches('[data-dr-ck-all]')) save(bar, card, true);
    else api(() => { setView(card, true); bar.showCookiesBanner(); setTimeout(() => fade(card.querySelector('.dr-ck__panel')), 700); });
  }, true);
})();

/* product gallery on phones: when only one photo is visible (one photo in total or for the chosen colour) it sits centred instead of in the peek position */
(() => {
  const check = (sl) => {
    const shown = [...sl.querySelectorAll('.dr-slider__slide.dr-product__media-item')].filter((s) => getComputedStyle(s).display !== 'none');
    sl.classList.toggle('dr-media-single', shown.length === 1);
  };
  const all = () => document.querySelectorAll('.dr-product-media__main dr-slider').forEach(check);
  const start = () => {
    all();
    document.querySelectorAll('.dr-product-media__main dr-slider').forEach((sl) => {
      if (sl.dataset.drSingleObs) return;
      sl.dataset.drSingleObs = '1';
      new MutationObserver(() => requestAnimationFrame(() => check(sl))).observe(sl, { subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  window.addEventListener('resize', all);
  document.addEventListener('change', (e) => { if (e.target.closest && e.target.closest('variant-picker, .dr-product-form, [class*="variant"]')) setTimeout(all, 120); });
})();
